// Version marker — bump this string each push to confirm in the browser
// console that you're running the latest deploy (vs a cached older bundle).
(window.HDLOG||function(){})('%c[HD] state-manager loaded — build 2026-05-28T10:00Z phase-5.5-consolidated', 'color:#7aff7a;font-weight:bold');

// Network diagnostic — Chrome/Edge expose connection info via Network Information API.
// effectiveType: '4g' = fast, '3g' = medium, '2g'/'slow-2g' = slow.
// downlink: estimated bandwidth in Mbps. rtt: round-trip latency in ms.
// saveData: true if user enabled data saver. If videos load slowly and downlink
// looks fine here, the bottleneck is elsewhere (CPU, decoder, server, or our code).
(function logNetInfo() {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) {
    (window.HDLOG||function(){})('[HD] navigator.connection NOT available (Safari/Firefox) — cannot measure connection');
    return;
  }
  const dump = () => (window.HDLOG||function(){})('[HD] connection |',
    'effectiveType:', c.effectiveType,
    '| downlink:', c.downlink + 'Mbps',
    '| rtt:', c.rtt + 'ms',
    '| saveData:', c.saveData,
    '| type:', c.type || 'n/a');
  dump();
  // Re-log if connection quality changes mid-session (e.g. wifi → cellular).
  if (typeof c.addEventListener === 'function') {
    c.addEventListener('change', () => { (window.HDLOG||function(){})('[HD] connection CHANGED'); dump(); });
  }
})();

class StateManager {
  constructor() {
    this.currentView = null;
    this.previousView = null; // Track previous view for world map restoration
    this.isTransitioning = false;
    this.TRANSITION_DURATION = 500;
    this.TRANSITION_TIMEOUT = 2000; // 2 second timeout for transitions
    // Pending user intent captured mid-transition. The transition window can
    // be long on a slow connection (we wait for the destination background
    // video's readyState), and dropping focus/unfocus clicks during it makes
    // the UI feel unresponsive. Instead we record the LAST intent here and
    // replay it the moment the in-flight transition finishes (see
    // _drainPendingIntent). Shape: { type: 'navigate', view } | { type: 'idle' }.
    this._pendingIntent = null;
    
    // Add mutex for main menu operations
    this.isMainMenuTransitioning = false;
    
    // Add timestamp tracking for debugging
    this.lastMainMenuCall = 0;
    
    // Store timeout references to prevent race conditions
    this.pendingTimeouts = new Map();
    // Pending element hides — keyed by HTMLElement, value is setTimeout id.
    // Every "fade out then display:none after Nms" hide must go through
    // _scheduleElementHide so it can be cancelled if the same element is
    // re-shown before the timer fires. See _scheduleElementHide for full reasoning.
    this._pendingElementHides = new Map();
    // Generation counter for navigation transitions. Bumped on every
    // navigateTo() and returnToIdle(). Any setTimeout that captures the old
    // generation and fires after a new nav started is a no-op. This is the
    // belt-and-suspenders safety net behind the per-element cancellation.
    this._navGeneration = 0;
    this.questScrollLockActive = false;
    this.merchantsDenScrollLockActive = false;
    this.mainMenuScrollLockActive = false;
    this._scrollLockCount = 0;
    this._scrollLockOriginalBodyOverflow = '';
    this._scrollLockOriginalHtmlOverflow = '';
    this._scrollLockOriginalBodyOverscroll = '';
    this._scrollLockOriginalHtmlOverscroll = '';
    this._deepLinkQuest = new URLSearchParams(window.location.search).get('quest') || null;
    this._deepLinkProduct = new URLSearchParams(window.location.search).get('den') || null;
    // world-map deep link (Discord / email / link-in-bio): ?view=world-map[&faction=<key>] or #world-map
    const _wmParams = new URLSearchParams(window.location.search);
    this._deepLinkWorldMap = _wmParams.get('view') === 'world-map' || window.location.hash === '#world-map';
    this._deepLinkFaction = _wmParams.get('faction') || null;
    // shop deep link (cart "Continue shopping"): ?view=merchants-den (or ?view=shop)
    // lands in the Shop view, skipping the title gate. Routes through the product
    // deep-link handler with no product, so it primes the shop bg the same way.
    this._deepLinkShop = _wmParams.get('view') === 'merchants-den' || _wmParams.get('view') === 'shop';
    // guide deep link (Discord "read the guide" links): ?view=guide
    this._deepLinkGuide = _wmParams.get('view') === 'guide';
    
    // Cache DOM elements with more detailed logging
    this.elements = {
      titleScreen: null, // Will be initialized dynamically
      productSelector: null, // Will be initialized dynamically
      header: document.querySelector('.hound-header'),
      rpgShops: [], // Will be initialized dynamically
      regularHeader: null, // Will be initialized dynamically
      guideSection: null, // Will be initialized dynamically
      questSection: null // Will be initialized dynamically
    };

    // Initialize elements that might be loaded dynamically
    this.initializeDynamicElements();

    // Ensure elements have transition properties
    this.initializeTransitions();

    // Initialize event listeners
    this.initializeEventListeners();

    if (this._deepLinkQuest) {
      const onReady = () => this._navigateToDeepLink();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        Promise.resolve().then(onReady);
      }
    } else if (this._deepLinkProduct) {
      const onReady = () => this._navigateToProductDeepLink();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        Promise.resolve().then(onReady);
      }
    } else if (this._deepLinkWorldMap) {
      const onReady = () => this._navigateToWorldMapDeepLink();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        Promise.resolve().then(onReady);
      }
    } else if (this._deepLinkShop) {
      const onReady = () => this._navigateToProductDeepLink();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        Promise.resolve().then(onReady);
      }
    } else if (this._deepLinkGuide) {
      const onReady = () => this._navigateToGuideDeepLink();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        Promise.resolve().then(onReady);
      }
    } else {
      // Lock scroll on DOMContentLoaded (not synchronously — iOS Safari won't
      // render the title-screen overlay if overflow:hidden is set before paint).
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.lockMainMenuScroll());
      } else {
        this.lockMainMenuScroll();
      }
    }
  }

  initializeDynamicElements() {
    // Try to find title screen with different selectors
    const titleScreenSelectors = [
      '.title-screen-container',
      '[id^="shopify-section-"][id*="__title_screen"]'
    ];

    for (const selector of titleScreenSelectors) {
      this.elements.titleScreen = document.querySelector(selector);
      if (this.elements.titleScreen) {
        break;
      }
    }

    // Try to find product selector with different selectors
    const productSelectorSelectors = [
      '[id^="shopify-section-"][id*="__product_selector_buttons"]',
      '.product-selector-buttons'
    ];

    for (const selector of productSelectorSelectors) {
      this.elements.productSelector = document.querySelector(selector);
      if (this.elements.productSelector) {
        (window.HDLOG||function(){})('StateManager: Found product selector with selector:', selector);
        break;
      }
    }

    // Try to find regular header with different selectors
    const regularHeaderSelectors = [
      '.header',
      'header-drawer',
      '.shopify-section-group-header-group'
    ];

    for (const selector of regularHeaderSelectors) {
      this.elements.regularHeader = document.querySelector(selector);
      if (this.elements.regularHeader) {
        break;
      }
    }

    // Find guide section
    this.elements.guideSection = document.querySelector('.guide-section');
    if (this.elements.guideSection) {
      (window.HDLOG||function(){})('StateManager: Found guide section:', {
        element: this.elements.guideSection,
        classes: this.elements.guideSection.classList.toString(),
        style: this.elements.guideSection.style.cssText
      });
      // Remove any inline styles that might interfere
      this.elements.guideSection.style = '';
    } else {
      console.warn('StateManager: Guide section not found!');
    }

    // Find quest section
    this.elements.questSection = document.querySelector('.quest-section');
    if (this.elements.questSection) {
      (window.HDLOG||function(){})('StateManager: Found quest section:', {
        element: this.elements.questSection,
        classes: this.elements.questSection.classList.toString(),
        style: this.elements.questSection.style.cssText
      });
      // Remove any inline styles that might interfere
      this.elements.questSection.style = '';
    } else {
      console.warn('StateManager: Quest section not found!');
    }

    // Initialize RPG shops
    this.updateRpgShops();

    // If elements still not found, wait for DOM changes
    if (!this.elements.titleScreen || !this.elements.productSelector || !this.elements.regularHeader) {
      const observer = new MutationObserver((mutations) => {
        let foundAll = true;

        if (!this.elements.titleScreen) {
          for (const selector of titleScreenSelectors) {
            const element = document.querySelector(selector);
            if (element) {
              (window.HDLOG||function(){})('StateManager: Title screen found via observer');
              this.elements.titleScreen = element;
              break;
            }
          }
          foundAll = foundAll && !!this.elements.titleScreen;
        }

        if (!this.elements.productSelector) {
          for (const selector of productSelectorSelectors) {
            const element = document.querySelector(selector);
            if (element) {
              this.elements.productSelector = element;
              break;
            }
          }
          foundAll = foundAll && !!this.elements.productSelector;
        }

        if (!this.elements.regularHeader) {
          for (const selector of regularHeaderSelectors) {
            const element = document.querySelector(selector);
            if (element) {
              (window.HDLOG||function(){})('StateManager: Regular header found via observer');
              this.elements.regularHeader = element;
              break;
            }
          }
          foundAll = foundAll && !!this.elements.regularHeader;
        }

        // Update RPG shops on any DOM change
        this.updateRpgShops();

        if (foundAll) {
          observer.disconnect();
          // Re-initialize transitions for newly found elements
          this.initializeTransitions();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  updateRpgShops() {
    // Find all RPG shop sections
    const rpgShopSections = document.querySelectorAll('[id^="shopify-section-"][id*="__rpg_shop_"]');
    
    // Update the elements array with new shops
    this.elements.rpgShops = Array.from(rpgShopSections);
    
    // Initialize transitions for any new shops.
    // 140ms, NOT the global 500ms: the shop card is near-black stone over a
    // bright scene, and a half-second opacity ramp shows a translucent grey
    // ghost of the card - filmed from Evan's recording (Aug 23) as "glitchy,
    // can't decide if the buttons should be showing". Scene/view crossfades
    // keep the long fade; a PRODUCT swap should snap like a game menu.
    this.elements.rpgShops.forEach(shop => {
      if (!shop.style.transition || shop.style.transition.indexOf('500ms') !== -1) {
        shop.style.transition = 'opacity 140ms ease-out';
      }
    });
  }

  initializeTransitions() {
    // Add transition properties to elements
    if (this.elements.titleScreen) {
      this.elements.titleScreen.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      this.elements.titleScreen.style.display = this._deepLinkQuest ? 'none' : 'flex';
    }
    if (this.elements.productSelector) {
      this.elements.productSelector.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      this.elements.productSelector.style.display = 'none';
    }
    if (this.elements.header) {
      this.elements.header.style.transition = `opacity ${this.TRANSITION_DURATION}ms ease`;
      this.elements.header.style.display = 'none';
    }
    this.elements.rpgShops.forEach(shop => {
      shop.style.transition = 'opacity 140ms ease-out'; // see refreshRpgShops note
      shop.style.display = 'none';
    });
  }

  initializeEventListeners() {
    // Listen for navigation events
    document.addEventListener('navigate', (e) => {
      this.navigateTo(e.detail.view);
    });

    // Listen for product selector changes
    document.addEventListener('productSelectorChange', (e) => {
      (window.HDLOG||function(){})('StateManager: Received product selector change:', e.detail);
      this.handleProductSelectorChange(e.detail);
    });

    window.addEventListener('resize', () => {
      if (this.currentView !== 'quest') return;
      if (this.isQuestMobileViewport()) {
        this.lockQuestPageScroll();
      } else {
        this.unlockQuestPageScroll();
      }
    });

    window.addEventListener('resize', () => {
      if (this.currentView !== 'merchants-den') return;
      if (!this.merchantsDenProductSelected) {
        if (this.isMerchantsDenMobileViewport()) {
          this.lockMerchantsDenScroll();
        } else {
          this.unlockMerchantsDenScroll();
        }
      }
    });
  }

  isQuestMobileViewport() {
    return window.matchMedia('(max-width: 1279px)').matches;
  }

  isMerchantsDenMobileViewport() {
    return window.matchMedia('(max-width: 1023px)').matches;
  }

  _acquireScrollLock() {
    if (this._scrollLockCount === 0) {
      this._scrollLockOriginalBodyOverflow = document.body.style.overflow;
      this._scrollLockOriginalHtmlOverflow = document.documentElement.style.overflow;
      this._scrollLockOriginalBodyOverscroll = document.body.style.overscrollBehavior;
      this._scrollLockOriginalHtmlOverscroll = document.documentElement.style.overscrollBehavior;
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    }
    this._scrollLockCount += 1;
  }

  _releaseScrollLock() {
    if (this._scrollLockCount <= 0) return;
    this._scrollLockCount -= 1;
    if (this._scrollLockCount !== 0) return;
    document.body.style.overflow = this._scrollLockOriginalBodyOverflow;
    document.documentElement.style.overflow = this._scrollLockOriginalHtmlOverflow;
    document.body.style.overscrollBehavior = this._scrollLockOriginalBodyOverscroll;
    document.documentElement.style.overscrollBehavior = this._scrollLockOriginalHtmlOverscroll;
    this._scrollLockOriginalBodyOverflow = '';
    this._scrollLockOriginalHtmlOverflow = '';
    this._scrollLockOriginalBodyOverscroll = '';
    this._scrollLockOriginalHtmlOverscroll = '';
  }

  lockMerchantsDenScroll() {
    if (this.merchantsDenScrollLockActive || !this.isMerchantsDenMobileViewport()) return;
    this._acquireScrollLock();
    this.merchantsDenScrollLockActive = true;
  }

  unlockMerchantsDenScroll() {
    if (!this.merchantsDenScrollLockActive) return;
    this._releaseScrollLock();
    this.merchantsDenScrollLockActive = false;
  }

  lockMainMenuScroll() {
    if (this.mainMenuScrollLockActive) return;
    this._acquireScrollLock();
    this.mainMenuScrollLockActive = true;
  }

  unlockMainMenuScroll() {
    if (!this.mainMenuScrollLockActive) return;
    this._releaseScrollLock();
    this.mainMenuScrollLockActive = false;
  }

  lockQuestPageScroll() {
    if (this.questScrollLockActive || !this.isQuestMobileViewport()) return;
    this._acquireScrollLock();
    this.questScrollLockActive = true;
  }

  unlockQuestPageScroll() {
    if (!this.questScrollLockActive) return;
    this._releaseScrollLock();
    this.questScrollLockActive = false;
  }

  async _navigateToDeepLink() {
    const slug = this._deepLinkQuest;
    this._deepLinkQuest = null;

    if (window._hideMainMenuVideo) window._hideMainMenuVideo();

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const videoId = isMobile ? 'quest-bg-default-mobile' : 'quest-bg-default-desktop';
    const vid = document.getElementById(videoId);

    if (!isMobile && vid && vid.tagName === 'VIDEO') {
      vid.style.display = 'block';
      vid.style.opacity = '0';
      vid.muted = true;
      vid.playsInline = true;
      if (window._mobileEnsureVideoSource) window._mobileEnsureVideoSource(vid);
      vid.load();
      vid.play().catch(() => {});

      await new Promise((resolve) => {
        let settled = false;
        const settle = () => { if (settled) return; settled = true; clearTimeout(cap); resolve(); };
        const cap = setTimeout(settle, 2000);
        if ('requestVideoFrameCallback' in vid) {
          vid.requestVideoFrameCallback(settle);
        } else {
          const onPlaying = () => requestAnimationFrame(settle);
          if (!vid.paused && vid.readyState >= 3) {
            requestAnimationFrame(settle);
          } else {
            vid.addEventListener('playing', onPlaying, { once: true });
          }
        }
      });
    }

    this._isDeepLinkNavigation = true;
    await this.navigateTo('quest');

    if (slug) {
      const questId = window.questSlugMap?.[slug];
      if (questId) {
        const questType = window.questData?.[questId]?.type;
        if (questType && typeof window.switchQuestTab === 'function') {
          window.switchQuestTab(questType);
        }
        if (typeof window.selectQuest === 'function') {
          window.selectQuest(questId);
        }
      }
    }

    const indicator = document.getElementById('quest-loading-indicator');
    if (indicator) {
      const minShowMs = 800;
      const elapsed = performance.now();
      const remaining = Math.max(0, minShowMs - elapsed);

      if (document.readyState !== 'complete') {
        await new Promise(resolve => {
          window.addEventListener('load', resolve, { once: true });
        });
      }

      if (remaining > 0) {
        await new Promise(r => setTimeout(r, remaining));
      }

      indicator.classList.add('fade-out');
      setTimeout(() => { indicator.style.display = 'none'; }, 400);
    }
  }

  async _navigateToProductDeepLink() {
    const handle = this._deepLinkProduct;
    this._deepLinkProduct = null;

    /* START THE GARMENT BEFORE THE ROOM. `?den=<handle>` is the ad landing
       path, and everything below this line - hiding the menu video, waiting up
       to two seconds for the den's background plate to paint its first frame,
       running the view transition - is about the ROOM. None of it is an input
       to fetching the product, yet the product used to wait behind all of it:
       measured on a cold 3840x2000 load the section markup landed at 2.4s and
       the model video did not start until 3.6s.
       Kicking the hydration off here costs nothing and overlaps it with the
       room. It is still awaited at exactly the same point below, so ordering
       and error handling are unchanged - the work simply starts earlier. The
       shopper gets their garment sooner, and the page stops holding the network
       open into what should be idle time. */
    /* Tell the scene a product is already on its way. Without this the den is
       briefly selection-less on a deep link, so the character power sync sees
       three unoccluded characters, arms their alpha loops and pulls 2MB of
       video that is forbidden to play about one second later when the card
       commits. The flag is cleared once the product has painted (or if the
       handle turns out to be bad), and the sync re-runs then, so a shopper who
       closes the card still gets the characters moving. */
    if (handle) document.documentElement.setAttribute('data-den-deeplink', 'pending');

    let deepLinkButton = null;
    let deepLinkHydration = null;
    if (handle && window.HoundDeferredSections) {
      deepLinkButton = document.querySelector(
        `.product-selector-button[data-product-handle="${handle}"]`);
      if (deepLinkButton) {
        try {
          deepLinkHydration = window.HoundDeferredSections
            .ensureProduct(deepLinkButton.getAttribute('data-product-id'));
          /* Attach a catch NOW so an early rejection is never an unhandled
             one; the await below still sees and reports the same rejection. */
          deepLinkHydration.catch(() => {});
        } catch (error) { deepLinkHydration = null; }
      }
    }

    if (window._hideMainMenuVideo) window._hideMainMenuVideo();

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const webpEl = isMobile ? document.getElementById('merchants-den-bg-webp-mobile') : null;

    if (webpEl) {
      if (!webpEl.complete) {
        await new Promise((resolve) => {
          webpEl.onload = resolve;
          webpEl.onerror = resolve;
          setTimeout(resolve, 2000);
        });
      }
    } else {
      const vid = document.getElementById('merchants-den-bg-video');
      if (vid && vid.tagName === 'VIDEO') {
        vid.style.display = 'block';
        vid.style.opacity = '0';
        vid.muted = true;
        vid.playsInline = true;
        if (window._mobileEnsureVideoSource) window._mobileEnsureVideoSource(vid);
        vid.load();
        vid.play().catch(() => {});

        await new Promise((resolve) => {
          let settled = false;
          const settle = () => { if (settled) return; settled = true; clearTimeout(cap); resolve(); };
          const cap = setTimeout(settle, 2000);
          if ('requestVideoFrameCallback' in vid) {
            vid.requestVideoFrameCallback(settle);
          } else {
            const onPlaying = () => requestAnimationFrame(settle);
            if (!vid.paused && vid.readyState >= 3) {
              requestAnimationFrame(settle);
            } else {
              vid.addEventListener('playing', onPlaying, { once: true });
            }
          }
        });
      }
    }

    this._isDeepLinkNavigation = true;
    await this.navigateTo('merchants-den');

    if (handle) {
      const btn = deepLinkButton ||
        document.querySelector(`.product-selector-button[data-product-handle="${handle}"]`);
      if (btn) {
        if (window.HoundDeferredSections) {
          try {
            /* Already in flight since the top of this method whenever the
               button and the loader were both present; otherwise start it now. */
            await (deepLinkHydration ||
              window.HoundDeferredSections.ensureProduct(btn.getAttribute('data-product-id')));
          } catch (error) {
            console.error('Could not load deep-linked product details:', error);
          }
        }
        btn.click();
        await this._waitForProductPaint(btn);
      }
      document.documentElement.removeAttribute('data-den-deeplink');
    }

    // Invalid/stale handles must fail open rather than leave the visitor behind
    // the cover until its global safety timeout. The deep-link marker clears on
    // the same principle: it suppresses character playback while it is set, so
    // it must never survive a path that did not reach the card.
    document.documentElement.removeAttribute('data-den-deeplink');
    document.documentElement.classList.remove('hd-deep-link-pending');
  }

  _waitForProductPaint(button) {
    const productId = button.getAttribute('data-product-id');
    const targetSection = Array.from(document.querySelectorAll('[id^="shopify-section-"][id*="__rpg_shop_"]')).find((section) => {
      const product = section.querySelector('[data-product-id]');
      return product && product.getAttribute('data-product-id') === productId;
    });
    if (!targetSection) return Promise.resolve();
    if (targetSection.__houndProductPaintPromise) return targetSection.__houndProductPaintPromise;

    /* THIS POLL HAD NO WAY OUT EXCEPT ITS OWN TIMEOUT.
       cardReady below runs a rAF loop that does a getComputedStyle and a
       getBoundingClientRect every frame, for up to 3.5 seconds, and the
       selector fires this and never awaits it. Switch products again before it
       settles and the superseded loop kept running against a section that had
       already been hidden - forced layout, every frame, on top of the loop the
       new switch had just started. Each switch supersedes the last one; a
       superseded generation stops on its next frame. */
    const paintGeneration = (this._productPaintGeneration || 0) + 1;
    this._productPaintGeneration = paintGeneration;
    const paintSuperseded = () => this._productPaintGeneration !== paintGeneration;

    const group = targetSection.querySelector('.product-modal-group');
    const video = targetSection.querySelector('video.product-video');
    const posterUrl = button.getAttribute('data-video-poster') || (video && video.poster) || '';

    const waitUntil = (test, timeoutMs) => new Promise((resolve) => {
      let complete = false;
      const finish = () => {
        if (complete) return;
        complete = true;
        clearTimeout(cap);
        resolve();
      };
      const poll = () => {
        if (complete) return;
        if (paintSuperseded()) { finish(); return; }
        let ready = false;
        try { ready = !!test(); } catch (error) {}
        if (ready) { finish(); return; }
        requestAnimationFrame(poll);
      };
      const cap = setTimeout(finish, timeoutMs);
      requestAnimationFrame(poll);
    });

    const cardReady = waitUntil(() => {
      if (!group || group.hasAttribute('data-rpg-awaiting-fit') || !group.hasAttribute('data-rpg-fit')) return false;
      const sectionStyle = getComputedStyle(targetSection);
      const rect = group.getBoundingClientRect();
      return document.documentElement.classList.contains('shop-has-selection') &&
        sectionStyle.display !== 'none' &&
        sectionStyle.visibility !== 'hidden' &&
        parseFloat(sectionStyle.opacity || '0') >= 0.98 &&
        rect.width > 0 && rect.height > 0;
    }, 3500);

    // Product information is independent of media readiness.
    const handoff = cardReady
      .then(() => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))))
      .then(() => {
        targetSection.removeAttribute('data-rpg-awaiting-product');
        if (group) clearTimeout(group.__rpgCompositionFallback);
        if (document.documentElement.getAttribute('data-shop-product-pending') === productId) {
          document.documentElement.classList.remove('shop-product-pending');
          document.documentElement.removeAttribute('data-shop-product-pending');
        }
      });

    targetSection.__houndProductPaintPromise = handoff;
    handoff.finally(() => {
      if (targetSection.__houndProductPaintPromise === handoff) {
        targetSection.__houndProductPaintPromise = null;
      }
    });
    return handoff;
  }

  async _navigateToWorldMapDeepLink() {
    const faction = this._deepLinkFaction;
    this._deepLinkWorldMap = false;
    this._deepLinkFaction = null;

    if (window._hideMainMenuVideo) window._hideMainMenuVideo();

    // The world-map section owns its own backdrop (canvas / realm grid), so no
    // destination bg-video to prime here — navigateTo handles the view swap and
    // the section's MutationObserver lazily boots on data-view="world-map".
    this._isDeepLinkNavigation = true;
    await this.navigateTo('world-map');

    // Optional faction sub-state: open that faction's shrine once the section
    // has exposed its hook. The section boots async after the view swap, so poll
    // briefly for the global before giving up (still lands on the map regardless).
    if (faction) {
      const key = String(faction).toLowerCase().trim();
      let tries = 0;
      const tryOpen = () => {
        if (typeof window.openFactionShrine === 'function') {
          window.openFactionShrine(key);
          return;
        }
        if (tries++ < 40) setTimeout(tryOpen, 100);
      };
      tryOpen();
    }
  }

  async _navigateToGuideDeepLink() {
    // ?view=guide: land straight in Guidance (mirrors the world-map deep link,
    // no sub-state). Added Jul 2026: guide links from Discord previously dumped
    // users on the title screen.
    this._deepLinkGuide = false;
    if (window._hideMainMenuVideo) window._hideMainMenuVideo();
    this._isDeepLinkNavigation = true;
    await this.navigateTo('guide');
  }

  handleProductSelectorChange(detail) {
    (window.HDLOG||function(){})('StateManager: Handling product selector change');
    
    // Update RPG shops list
    this.updateRpgShops();
    
    // Find the active shop
    const activeShop = this.elements.rpgShops.find(shop => shop.style.display !== 'none' && shop.style.opacity === '1');
    if (!activeShop) {
      (window.HDLOG||function(){})('StateManager: No active shop found');
      return;
    }

    // Update height after a delay to ensure content is rendered
    setTimeout(() => {
      const activeContent = activeShop.querySelector('.activeContent');
      if (activeContent) {
        (window.HDLOG||function(){})('StateManager: Updating height for active content');
        if (typeof updateHeight === 'function') {
          updateHeight();
        } else {
          console.warn('StateManager: updateHeight function not found');
        }
      }
    }, 100);
  }

  async navigateTo(view) {
    if (window.HoundArmViewMedia) window.HoundArmViewMedia(view);
    (window.HDLOG||function(){})('[HD] navigateTo →', view, '| from:', this.currentView, '| navGen:', this._navGeneration);
    if (this.currentView === view) {
      return;
    }
    if (this.isTransitioning) {
      this._pendingIntent = { type: 'navigate', view: view };
      (window.HDLOG||function(){})('[HD] navigateTo QUEUED (mid-transition) →', view, '| will replay after current transition');
      return;
    }
    if (this.currentView === 'main-menu' && view !== 'merchants-den' && view !== 'guide' && view !== 'world-map' && view !== 'quest') {
      return;
    }
    this.previousView = this.currentView;
    // Bump generation so any in-flight hide timers from the previous navigation
    // become no-ops when they fire. This is the global safety net behind the
    // per-element _cancelElementHide calls in each show method.
    this._navGeneration++;
    if ((this.previousView === 'main-menu' || this.previousView === null) && view !== 'main-menu') {
      this.unlockMainMenuScroll();
    }
    // Note: 'merchants-den' is the internal view name; displayed as "Shop" in the UI
    // (The transitionMap / transitionSequenceMap that used to select pan/zoom
    // transition CLIPS for each view pair were removed Jul 24 2026 along with
    // the clips themselves — the parallax scene performs every transition.)

    // Bring the parallax scene up if this entry didn't come through a path that
    // already did (e.g. the GLOBAL HEADER NAV on the title screen, which is how
    // you arrive from a product page's "Enter the World"). Without it,
    // mobile/narrow viewports rendered the view over a black background.
    // MUST run BEFORE the setView() below: activateIdle() sets data-view='idle'
    // and nulls currentView, so calling it later clobbers the destination view.
    if (window.parallaxScene && typeof window.parallaxScene.ensureActive === 'function') {
      window.parallaxScene.ensureActive(view);
    }

    this.isTransitioning = true;

    // ── Signal the new view via <html data-view> synchronously at the
    // START of the transition. This is the SINGLE setView call per nav.
    //
    // Why at the start (not the end)? Section CSS keys off data-view. For
    // show transitions, the new view's CSS must apply BEFORE the imperative
    // showXxx() runs so the CSS fade-in is already in flight. For hide
    // transitions, the OLD view's CSS must STOP applying as soon as the
    // user signals intent so the fade-out begins immediately. Setting it at
    // the end (as the original code did) left a window where old and new
    // CSS were ambiguous — flicker / stuck-visible state.
    if (window.viewManager) {
      window.viewManager.setView(view, { reason: 'navigateTo' });
    }

    if (window._mobilePreWarmForView) {
      window._mobilePreWarmForView(view);
    }
    try {
      const applyRpgGradient = () => {
        if (view !== 'world-map') {
          document.documentElement.style.setProperty('--rpg-bg-gradient-opacity', view === 'quest' ? '1' : '0');
        }
      };
      const getDestinationBackgroundVideoId = (targetView) => {
        const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
        if (targetView === 'guide') {
          return isMobileViewport ? 'guide-bg-talking-mobile' : 'guide-bg-talking-desktop';
        }
        if (targetView === 'merchants-den') {
          if (isMobileViewport && document.getElementById('merchants-den-bg-webp-mobile')) {
            return 'merchants-den-bg-webp-mobile';
          }
          return 'merchants-den-bg-video';
        }
        if (targetView === 'quest') {
          return isMobileViewport ? 'quest-bg-default-mobile' : 'quest-bg-default-desktop';
        }
        return null;
      };
      const waitForDestinationBackgroundReady = async (targetView) => {
        const destinationVideoId = getDestinationBackgroundVideoId(targetView);
        if (!destinationVideoId) return;
        const destinationEl = document.getElementById(destinationVideoId);
        if (!destinationEl) return;

        if (destinationEl.tagName === 'IMG') {
          if (!destinationEl.complete) {
            await new Promise((resolve) => {
              const timeout = setTimeout(resolve, 1000);
              destinationEl.addEventListener('load', () => {
                clearTimeout(timeout);
                resolve();
              }, { once: true });
            });
          }
          await new Promise((resolve) => {
            requestAnimationFrame(() => { requestAnimationFrame(resolve); });
          });
          return;
        }

        const destinationVideo = destinationEl;
        // Step 1: wait until there is enough data to begin rendering.
        if (destinationVideo.readyState < 2) {
          await new Promise((resolve) => {
            let settled = false;
            const settle = () => {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              destinationVideo.removeEventListener('loadeddata', settle);
              destinationVideo.removeEventListener('canplay', settle);
              resolve();
            };
            const timeout = setTimeout(settle, 1000);
            destinationVideo.addEventListener('loadeddata', settle, { once: true });
            destinationVideo.addEventListener('canplay', settle, { once: true });
          });
        }

        // Step 2: wait for an actually composited frame before removing transition videos.
        await new Promise((resolve) => {
          let settled = false;
          let onPlaying = null;
          const settle = () => {
            if (settled) return;
            settled = true;
            clearTimeout(hardCap);
            if (onPlaying) {
              destinationVideo.removeEventListener('playing', onPlaying);
            }
            resolve();
          };

          const hardCap = setTimeout(settle, 500);

          if ('requestVideoFrameCallback' in destinationVideo) {
            destinationVideo.requestVideoFrameCallback(() => {
              settle();
            });
            return;
          }

          onPlaying = () => {
            requestAnimationFrame(() => {
              settle();
            });
          };

          if (!destinationVideo.paused && destinationVideo.readyState >= 3) {
            requestAnimationFrame(() => {
              settle();
            });
            return;
          }

          destinationVideo.addEventListener('playing', onPlaying, { once: true });
        });
      };
      // Keep a global view marker for CSS (and fixed background overlays).
      // For the world map overlay, we intentionally keep the existing background styling.
      if (view !== 'world-map') {
        document.documentElement.setAttribute('data-rpg-view', view);
      }

      document.body.offsetHeight;
      let postTransitionBlueFadeOut = false;

      // The parallax scene performs the visual transition between views via
      // focusCharacter(). Every transition (Shop↔Quest, Shop↔Guide, Idle→Shop,
      // …) now takes one uniform code path, so re-entry state is consistent.
      const parallaxIsActive = !!(
        window.parallaxScene &&
        typeof window.parallaxScene.isActive === 'function' &&
        window.parallaxScene.isActive()
      );

      // ── Single transition path ────────────────────────────────────────
      // The parallax scene owns every view transition visually. The legacy
      // transition-VIDEO machinery that used to run here (playSequence over
      // 16 pan/zoom clips, playTransition, bgReadyPromise waits, fade-to-blue
      // choreography, hideAllTransitions) was removed Jul 24 2026 — it only
      // ever ran when the scene wasn't active, which is exactly the broken
      // state we now prevent via parallaxScene.ensureActive(). Its clips are
      // gone from fixed-video-background.liquid too.
      {
        // No transition video — direct path. Used for:
        //   (a) parallax-active transitions (parallax handles the visual swap)
        //   (b) main-menu transitions
        //   (c) any view pair without a transition map entry (e.g. null→view)
        //
        // Pre-hide the outgoing view UI synchronously so it doesn't visually
        // overlap the incoming view during the rAF wait + showXxx fade-in.
        // hideUnnecessaryElements (inside showXxx) does this too, but only
        // AFTER the toggle functions have already focused the new character —
        // leading to a brief frame where both old UI and new parallax focus
        // are visible. Doing it here, BEFORE the toggle, gives a clean handoff.
        (window.HDLOG||function(){})('[HD] navigateTo ELSE | parallaxActive:', parallaxIsActive);
        if (this.elements.guideSection && this.currentView === 'guide' && view !== 'guide') {
          // Phase 5: visibility is CSS-driven. Clear inline overrides.
          this.elements.guideSection.classList.remove('visible');
          this.elements.guideSection.style.opacity = '';
          this.elements.guideSection.style.pointerEvents = '';
        }
        if (this.elements.questSection && this.currentView === 'quest' && view !== 'quest') {
          // Phase 4: visibility is CSS-driven. Clear inline overrides.
          this.elements.questSection.classList.remove('visible');
          this.elements.questSection.style.opacity = '';
          this.elements.questSection.style.pointerEvents = '';
        }
        if (this.elements.productSelector && this.currentView === 'merchants-den' && view !== 'merchants-den') {
          this.elements.productSelector.style.opacity = '0';
          this.elements.productSelector.style.pointerEvents = 'none';
          // Use cancellable hide — _resetRpgShopState() in showMerchantsDen
          // will cancel this if user navigates back to shop before it fires.
          this._scheduleElementHide(this.elements.productSelector, () => {
            this.elements.productSelector.style.display = 'none';
          }, this.TRANSITION_DURATION);
          // Also hide any currently-visible RPG sub-panel.
          this.elements.rpgShops.forEach((shop) => {
            if (shop.style.display !== 'none' && shop.style.opacity !== '0') {
              shop.style.opacity = '0';
              this._scheduleElementHide(shop, () => {
                shop.style.display = 'none';
              }, this.TRANSITION_DURATION);
            }
          });
        }

        /* CUE THE BEAT BEFORE ANYTHING IS TOLD TO MOVE.
           The director's table decides where the merchant stands, whether the
           street carries its depth of field, whether the cast is alive and at
           what scale the card may reveal. All four are read by the calls
           immediately below (toggleMerchantsDenBgVideo -> focusCharacter reads
           its slot; the fitter's applier prerolls the fleet), so the beat has
           to be in force before them or each owner answers from the previous
           scene. Entering the den is the MARKET beat: the market is open and
           nothing has been picked up yet. Leaving it returns to TITLE.

           THE GUIDE AND THE QUEST BOARD ARE BEATS TOO. They give a character
           focus exactly as the den does, so they are rows of the same table
           rather than views that decide for themselves - and, like the den's,
           their beat must be in force BEFORE the toggle calls below, because
           those call focusCharacter, which asks the director which slot the
           character stands in and which character the beat is about. Anything
           not in the table returns to TITLE, which is what the world map and
           the main menu already did. */
        if (window.HoundDenDirector) {
          const DEN_BEAT_FOR_VIEW = {
            'merchants-den': 'market',
            guide: 'guide',
            quest: 'quest'
          };
          window.HoundDenDirector.setBeat(
            DEN_BEAT_FOR_VIEW[view] || 'title', 'navigate:' + view);
        }

        // For world map overlay, DO NOT change the background; keep whatever was visible
        applyRpgGradient();
        if ((view === 'guide' || view === 'merchants-den' || view === 'quest') && window._hideMainMenuVideo) {
          window._hideMainMenuVideo();
        }
        if (view === 'guide') {
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);
          if (window.toggleQuestBgVideo) window.toggleQuestBgVideo('none');
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('talking');
        } else if (view === 'merchants-den') {
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
          if (window.toggleQuestBgVideo) window.toggleQuestBgVideo('none');
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(true);
        } else if (view === 'quest') {
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
          if (window.toggleQuestBgVideo) window.toggleQuestBgVideo('show');
        } else if (view === 'world-map') {
          // leave backgrounds as-is
        } else {
          if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
          if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);
          if (window.toggleQuestBgVideo) window.toggleQuestBgVideo('none');
        }
      }
      
      await new Promise(resolve => requestAnimationFrame(resolve));
      switch (view) {
        case 'main-menu':
          await this.showMainMenu();
          break;
        case 'merchants-den':
          await this.showMerchantsDen();
          break;
        case 'guide':
          await this.showGuide();
          break;
        case 'quest':
          await this.showQuest();
          break;
        case 'world-map':
          await this._displayWorldMap();
          break;
      }

      if (postTransitionBlueFadeOut && view === 'quest' && window.rpgBlueOverlay) {
        window.rpgBlueOverlay.set({ visible: true, opacity: 0, durationMs: 300 });
      }

      this.currentView = view;
      if (window._mobileWarmTransitions) window._mobileWarmTransitions(view);
      const event = new CustomEvent('navigationComplete', { detail: { view: view } });
      document.dispatchEvent(event);

      // NOTE: ViewManager.setView(view) is called ONCE, at the START of
      // navigateTo (search "navigateTo start"). It is synchronous and
      // idempotent, so there is no second call here — the data-view
      // attribute has been correct since the start of this transition.
    } catch (error) {
      console.error('[NAV] Error during navigation:', error);
      this.currentView = this.previousView;
    } finally {
      this.isTransitioning = false;
      // Replay any focus/unfocus intent the user expressed mid-transition.
      this._drainPendingIntent();
    }
  }

  /**
   * Replay the last user intent (navigate / return-to-idle) that arrived while
   * a transition was in flight. Called from the finally block of BOTH
   * navigateTo() and returnToIdle(), so whichever transition was running, the
   * queued intent fires as soon as it settles. "Last intent wins" — only one
   * slot, so rapid clicks collapse to the final one the user expressed.
   */
  _drainPendingIntent() {
    const intent = this._pendingIntent;
    this._pendingIntent = null;
    if (!intent) return;
    (window.HDLOG||function(){})('[HD] draining pending intent →', intent.type, intent.view || '');
    // Defer to a microtask so isTransitioning has fully settled and we don't
    // recurse inside the current finally.
    Promise.resolve().then(() => {
      if (intent.type === 'navigate') {
        if (intent.view && intent.view !== this.currentView) this.navigateTo(intent.view);
      } else if (intent.type === 'idle') {
        if (this.currentView) this.returnToIdle();
      }
    });
  }

  /**
   * Schedule a deferred hide on a specific DOM element (e.g.
   * "fade element out, then set display:none after TRANSITION_DURATION").
   *
   * Why this exists: the old pattern was raw setTimeout with a `currentView !==`
   * guard checked when the timer fires. That guard reads state at fire time
   * (wall-clock), but currentView is set LATE inside the show flow (after
   * display:flex + the opacity transition has started). When a leaving-shop
   * transition raced an entering-shop transition, the stale display:none timer
   * fired while the new showMerchantsDen() was still mid-transition — the
   * guard saw the old currentView, passed, and clobbered the just-shown UI.
   * Result: shop UI invisible after spam-clicking nav.
   *
   * The fix is explicit per-element cancellation. Each scheduled hide is
   * tracked in _pendingElementHides keyed by the element. Re-scheduling on
   * the same element cancels the previous timer. Calling _cancelElementHide
   * before showing the element guarantees no stale display:none fires later.
   *
   * Belt-and-suspenders: a generation counter is captured at schedule time.
   * If the navigation generation has advanced when the timer fires, the timer
   * is a no-op. This catches any path that bypasses _cancelElementHide.
   */
  _scheduleElementHide(element, hideAction, delayMs) {
    if (!element) return;
    // Cancel any existing pending hide on this element so we don't pile up.
    if (this._pendingElementHides.has(element)) {
      clearTimeout(this._pendingElementHides.get(element));
    }
    const gen = this._navGeneration;
    const id = setTimeout(() => {
      this._pendingElementHides.delete(element);
      if (gen !== this._navGeneration) {
        // A new navigation started after this hide was scheduled — the world
        // has moved on, and the show side is responsible for the element now.
        return;
      }
      try {
        hideAction();
      } catch (e) {
        console.warn('[HIDE] hideAction threw:', e);
      }
    }, delayMs);
    this._pendingElementHides.set(element, id);
  }

  /**
   * Cancel any pending deferred hide on the given element. Call this in
   * every show path BEFORE setting display:flex / opacity:1, so a stale
   * display:none timer can't fire after the show completes.
   */
  _cancelElementHide(element) {
    if (!element) return;
    if (this._pendingElementHides.has(element)) {
      clearTimeout(this._pendingElementHides.get(element));
      this._pendingElementHides.delete(element);
    }
  }

  /**
   * Hard reset all RPG shop sections to the canonical hidden state.
   * Called at the start of showMerchantsDen() so re-entry is deterministic
   * regardless of which path got us here (header click, char click, navigate
   * from idle after returnToIdle, navigate from another view).
   *
   * Background: the shop sub-panel breakage on certain nav sequences (e.g.
   * Shop → Quest → Guide → returnToIdle → Shop) was reproducible because
   * the inner product video and info card could be left in a half-initialized
   * state when the user re-opens a panel. The button-handler in
   * product-selector-buttons.liquid resets its own state on click, but state
   * that lives on the <video> element itself (paused mid-load, error state,
   * stale readyState) survives across hide/show. Forcing each video back to
   * the canonical "paused at currentTime=0, freshly loaded" state on every
   * shop entry eliminates this entire class of bugs.
   *
   * Also dispatches a `shopViewEntered` event so the product selector script
   * can reset its own internal closure state (lastSelectedFrame, etc.)
   * without having to introspect state-manager internals.
   */
  _resetRpgShopState() {
    const shops = document.querySelectorAll('[id^="shopify-section-"][id*="__rpg_shop_"]');
    (window.HDLOG||function(){})('[HD] _resetRpgShopState | shops:', shops.length);
    shops.forEach(shop => {
      // Cancel any pending deferred hide so the canonical state we set below sticks.
      this._cancelElementHide(shop);

      // Canonical hidden state. Clear visibility too in case a prior view (quest)
      // set it to hidden via the hard-hide path in hideUnnecessaryElements.
      // Don't touch pointer-events — the rpg-shop CSS owns it (wrapper:none,
      // inner panel:auto). Setting inline 'none' here would block the CSS
      // rule from re-enabling pointer events on the panel on the next show.
      shop.style.opacity = '0';
      shop.style.display = 'none';
      shop.style.pointerEvents = '';
      shop.style.visibility = '';

      // Reset every video element inside this shop to a known-good paused/loaded
      // state. Without this, videos that were mid-buffer / errored / paused at
      // an arbitrary currentTime when the section was hidden can fail to render
      // when the user re-opens the panel later.
      shop.querySelectorAll('video').forEach(video => {
        // Clear any visibility:hidden left by the mobile-video path in
        // product-selector-buttons when hideLoading() never fired.
        video.style.visibility = '';
        // Pause and seek to start. Do NOT call video.load() here — that drops
        // readyState back to 0 and triggers a full re-download for all 12+ shop
        // videos every time the user enters merchants-den, competing with
        // background videos and causing slow loads. The button-click handler
        // already calls load() conditionally (readyState < 2), and the
        // readyStateSnapshot fix in playVideo() handles the seek-induced
        // readyState drop correctly. Error state recovery is handled by the
        // button-click safety-check interval.
        try {
          if (!video.paused) video.pause();
          if (video.readyState > 0) video.currentTime = 0;
        } catch (e) {
          // ignore — video might not be in a seekable state yet
        }
      });

      // Ensure loading indicator is hidden so it doesn't cover the info card
      // on re-entry if hideLoading() was skipped by a stale isLatestRequest().
      shop.querySelectorAll('.loading-indicator').forEach(li => {
        li.classList.add('hidden');
      });
    });

    // Tell the product-selector-buttons script to reset its own closure-held
    // state (lastSelectedFrame, currentRequestId-driven pending ops). It listens
    // via document for this event.
    document.dispatchEvent(new CustomEvent('shopViewEntered'));
    (window.HDLOG||function(){})('[HD] _resetRpgShopState done | shopViewEntered dispatched');
  }

  /**
   * Return to idle state — no view active, but parallax scene stays visible.
   *
   * Triggered by parallax doc-click on the scene background. This is the proper
   * inverse of navigateTo(): it owns hiding view UI and resetting state-manager
   * fields together. Before this method existed, the parallax mutated
   * this.currentView directly, leaving other internal state (locks, flags,
   * isTransitioning) out of sync — which caused the shop sub-panel to render
   * in a broken state on re-entry (missing model video, missing info card).
   *
   * Key invariants:
   *  - We never set display:none on view UI. Only opacity:0 + pointer-events:none.
   *    Keeping sections in the layout preserves their inner iframe/video/script
   *    state across hide/re-show cycles. The state-manager's show paths (e.g.
   *    showMerchantsDen) already tolerate display already being flex.
   *  - We reset currentView and isTransitioning together inside try/finally so
   *    a re-entry navigate that races with the cleanup sees a coherent state.
   *  - Parallax visual unfocus is delegated to window.parallaxScene.clearFocus().
   *    The scene STAYS visible; we are not deactivating the parallax.
   */
  async returnToIdle() {
    (window.HDLOG||function(){})('[HD] returnToIdle | from:', this.currentView, '| transitioning:', this.isTransitioning);
    if (!this.currentView) return;
    if (this.isTransitioning) {
      // Don't drop the unfocus — replay it when the in-flight transition ends.
      this._pendingIntent = { type: 'idle' };
      (window.HDLOG||function(){})('[HD] returnToIdle QUEUED (mid-transition) | will replay after current transition');
      return;
    }
    const fromView = this.currentView;

    // Bump generation so any in-flight hide timers from the previous navigation
    // become no-ops when they wake up. Must happen BEFORE we cancel/schedule
    // any new hides so the cancelled timers see the new generation if they
    // somehow escape per-element cancellation.
    this._navGeneration++;

    this.isTransitioning = true;

    // ── Phase 4 architecture: signal idle transition via data-view at the
    // START (consistent with navigateTo). CSS rules for the OLD view's
    // section stop matching → fade-out begins immediately. The imperative
    // hide block below becomes redundant for CSS-converted sections (quest
    // and beyond) but stays in place for not-yet-converted sections.
    if (window.viewManager) {
      window.viewManager.setView('idle', { reason: 'state-manager.returnToIdle start' });
    }
    /* Leaving the den by unfocusing rather than by navigating is still leaving
       it: the composition returns to the TITLE row, so the street loses its
       depth of field and the cast comes back up with it. */
    if (window.HoundDenDirector) {
      window.HoundDenDirector.setBeat('title', 'returnToIdle');
    }

    try {
      // Reset view state FIRST so any incoming navigate (e.g. user clicks a char
      // while we're still finishing cleanup) sees currentView=null and proceeds.
      this.previousView = fromView;
      this.currentView = null;
      document.documentElement.removeAttribute('data-rpg-view');
      this.merchantsDenProductSelected = false;
      this.unlockMerchantsDenScroll();
      this.unlockQuestPageScroll();

      // Cancel any pending deferred hides — we are explicitly setting the
      // hidden state now, no need for a stale display:none to fire later.
      if (this.elements.productSelector) {
        this._cancelElementHide(this.elements.productSelector);
        this.elements.productSelector.style.opacity = '0';
        this.elements.productSelector.style.pointerEvents = 'none';
      }
      document.querySelectorAll('[id*="__rpg_shop_"]').forEach(shop => {
        this._cancelElementHide(shop);
        shop.style.opacity = '0';
        // Pointer-events managed by CSS — see comment in _resetRpgShopState.
        shop.style.pointerEvents = '';
      });
      const questSection = document.querySelector('.quest-section');
      if (questSection) {
        // Phase 4: visibility is CSS-driven. Clear inline overrides so the
        // html[data-view] rule wins. ViewManager.setView('idle') below sets
        // data-view to 'idle' → quest's html[data-view="quest"] rule no
        // longer matches → CSS fade-out kicks in.
        questSection.classList.remove('visible');
        questSection.style.opacity = '';
        questSection.style.pointerEvents = '';
      }
      const guideSection = document.querySelector('.guide-section');
      if (guideSection) {
        // Phase 5: visibility is CSS-driven via data-view. ViewManager.setView('idle')
        // at the start of returnToIdle already changed data-view → CSS fade-out
        // is in flight. Just clear inline overrides + the legacy class.
        guideSection.classList.remove('visible');
        guideSection.style.opacity = '';
        guideSection.style.pointerEvents = '';
      }

      // Tell parallax to clear focus visually. Scene stays visible.
      if (window.parallaxScene && typeof window.parallaxScene.clearFocus === 'function') {
        window.parallaxScene.clearFocus();
      }

      document.dispatchEvent(new CustomEvent('viewChange', {
        detail: { from: fromView, to: null }
      }));

      // ViewManager.setView('idle') already called at the START of this
      // method (line ~1219). Idempotent — no need to call again here.
      (window.HDLOG||function(){})('[HD] returnToIdle done');
    } finally {
      this.isTransitioning = false;
      // Replay any focus/unfocus intent the user expressed mid-transition.
      this._drainPendingIntent();
    }
  }

  async hideAllSections() {
    (window.HDLOG||function(){})('StateManager: Hiding all sections');
    const promises = [];

    // Hide title screen
    if (this.elements.titleScreen) {
      (window.HDLOG||function(){})('StateManager: Hiding title screen');
      this.elements.titleScreen.style.opacity = '0';
      this.elements.titleScreen.style.pointerEvents = 'none';
      promises.push(this.waitForTransition(this.elements.titleScreen).then(() => {
        this.elements.titleScreen.style.display = 'none';
      }));
    }

    // Hide product selector
    if (this.elements.productSelector) {
      (window.HDLOG||function(){})('StateManager: Hiding product selector');
      this.elements.productSelector.style.opacity = '0';
      promises.push(this.waitForTransition(this.elements.productSelector).then(() => {
        this.elements.productSelector.style.display = 'none';
      }));
    }

    // Hide RPG header (hound-header)
    if (this.elements.header) {
      (window.HDLOG||function(){})('StateManager: Hiding RPG header');
      this.elements.header.style.opacity = '0';
      this.elements.header.style.pointerEvents = 'none';
      promises.push(this.waitForTransition(this.elements.header).then(() => {
        this.elements.header.style.display = 'none';
      }));
    }

    // Hide guide section
    if (this.elements.guideSection) {
      (window.HDLOG||function(){})('StateManager: Hiding guide section');
      this.elements.guideSection.classList.remove('visible');
      promises.push(this.waitForTransition(this.elements.guideSection).then(() => {
        // No need to set display: none as it's handled by CSS
      }));
    }

    // Hide quest section
    if (this.elements.questSection) {
      (window.HDLOG||function(){})('StateManager: Hiding quest section');
      this.elements.questSection.classList.remove('visible');
      promises.push(this.waitForTransition(this.elements.questSection).then(() => {
        // No need to set display: none as it's handled by CSS
      }));
    }

    // Update RPG shops before hiding them
    this.updateRpgShops();

    // Hide all RPG shops
    (window.HDLOG||function(){})('StateManager: Hiding RPG shops');
    this.elements.rpgShops.forEach(shop => {
      shop.style.opacity = '0';
      promises.push(this.waitForTransition(shop).then(() => {
        shop.style.display = 'none';
      }));
    });

    if (window.toggleMerchantsDenBgVideo) window.toggleMerchantsDenBgVideo(false);

    await Promise.all(promises);
    (window.HDLOG||function(){})('StateManager: All sections hidden');
  }

  async showMainMenu() {
    // Mutex protection to prevent concurrent execution
    if (this.isMainMenuTransitioning) {
      this.logWithTimestamp('🚫 Main menu transition already in progress, skipping request', {
        isMainMenuTransitioning: this.isMainMenuTransitioning,
        currentView: this.currentView,
        titleScreenDisplay: this.elements.titleScreen?.style.display,
        titleScreenOpacity: this.elements.titleScreen?.style.opacity
      });
      return;
    }

    if (this.currentView === 'main-menu') {
      this.logWithTimestamp('🚫 Already on main menu, skipping transition', {
        currentView: this.currentView,
        titleScreenDisplay: this.elements.titleScreen?.style.display,
        titleScreenOpacity: this.elements.titleScreen?.style.opacity
      });
      return;
    }

    this.logWithTimestamp('🎯 Starting showMainMenu transition', {
      currentView: this.currentView,
      isMainMenuTransitioning: this.isMainMenuTransitioning,
      titleScreenDisplay: this.elements.titleScreen?.style.display,
      titleScreenOpacity: this.elements.titleScreen?.style.opacity,
      titleScreenPointerEvents: this.elements.titleScreen?.style.pointerEvents
    });
    
    this.isMainMenuTransitioning = true;
    this.logWithTimestamp('🔒 Main menu mutex locked');
    
    try {
      this.logWithTimestamp('📦 About to call hideUnnecessaryElements("main-menu")');
      await this.hideUnnecessaryElements('main-menu');
      this.logWithTimestamp('✅ hideUnnecessaryElements completed');
      
      this.logWithTimestamp('🎨 Setting up title screen display');
      if (this.elements.titleScreen) {
        this.logWithTimestamp('   Title screen element found, setting styles');
        this.logWithTimestamp('   Before setting styles', {
          display: this.elements.titleScreen.style.display,
          opacity: this.elements.titleScreen.style.opacity,
          pointerEvents: this.elements.titleScreen.style.pointerEvents
        });
        
        this.elements.titleScreen.style.display = 'flex';
        this.elements.titleScreen.style.opacity = '1';
        this.elements.titleScreen.style.pointerEvents = 'auto';

        // elements.titleScreen is the OUTER Shopify section wrapper. The desktop
        // parallax scene (Continue Game -> parallax:enter -> activateIdle) hides
        // the INNER .title-screen-container (adds .fade-out + display:none), which
        // this restore doesn't touch — so the menu's logo + buttons stay hidden
        // inside a visible-but-empty section. Explicitly restore the inner
        // container too whenever we return to the main menu.
        const tsInner = document.querySelector('.title-screen-container');
        if (tsInner) {
          tsInner.classList.remove('fade-out');
          tsInner.style.display = 'flex';
          tsInner.style.opacity = '1';
          tsInner.style.pointerEvents = 'auto';
          tsInner.style.transform = '';
        }

        this.logWithTimestamp('   After setting styles', {
          display: this.elements.titleScreen.style.display,
          opacity: this.elements.titleScreen.style.opacity,
          pointerEvents: this.elements.titleScreen.style.pointerEvents
        });
      } else {
        this.logWithTimestamp('⚠️ Title screen element not found!');
      }

      this.logWithTimestamp('👤 Updating header visibility (hiding headers)');
      this.updateHeaderVisibility(false);
      
      this.logWithTimestamp('🔄 Setting currentView to main-menu');
      this.currentView = 'main-menu';
      this.lockMainMenuScroll();
      if (window._showMainMenuVideo) window._showMainMenuVideo();

      this.logWithTimestamp('🎯 showMainMenu transition completed successfully', {
        currentView: this.currentView,
        titleScreenDisplay: this.elements.titleScreen?.style.display,
        titleScreenOpacity: this.elements.titleScreen?.style.opacity,
        titleScreenPointerEvents: this.elements.titleScreen?.style.pointerEvents
      });

      // Background video logic moved to the end of the method
    } catch (error) {
      this.logWithTimestamp('❌ Error in showMainMenu', error);
      console.error('   Error stack:', error.stack);
    } finally {
      // Always clear the mutex, even if there was an error
      this.logWithTimestamp('🔓 Clearing main menu mutex');
      this.isMainMenuTransitioning = false;
    }

    // Ensure background is only set if main-menu is still the current view
    if (this.currentView === 'main-menu') {
      this.logWithTimestamp('[BG] Setting background for main menu');
      if (window.toggleGuideBgVideo) {
        this.logWithTimestamp('[BG] Hiding guide background (toggleGuideBgVideo none)');
        window.toggleGuideBgVideo('none');
      }
      if (window.toggleMerchantsDenBgVideo) {
        this.logWithTimestamp('[BG] Hiding merchants-den background (toggleMerchantsDenBgVideo false)');
        window.toggleMerchantsDenBgVideo(false);
      }
    }
  }

  async showMerchantsDen() {
    if (this.currentView === 'merchants-den') {
      (window.HDLOG||function(){})('Already in merchant\'s den, skipping transition');
      return;
    }

    (window.HDLOG||function(){})('Showing merchant\'s den');
    this.merchantsDenProductSelected = false;
    this.lockMerchantsDenScroll();

    // PHASE 0: Hard-reset RPG shop state. This guarantees the inner product
    // video and info card render correctly on every entry — even after
    // pathological nav sequences like Shop→Quest→Guide→returnToIdle→Shop where
    // shop sub-panel videos could be left in a half-initialized state.
    // See _resetRpgShopState() docstring for the full reasoning.
    this._resetRpgShopState();

    // PHASE 1: Prepare sections
    await this.hideUnnecessaryElements('merchants-den');
    
    // Background video is now handled in the transition logic
    // PHASE 2-3: Background video preparation and transition video hiding removed
    
    // PHASE 4: Show product selector with proper fade-in
    if (this.elements.productSelector) {
      (window.HDLOG||function(){})('[HD] showMerchantsDen PHASE4 | before:', 'display=' + this.elements.productSelector.style.display, 'opacity=' + this.elements.productSelector.style.opacity);

      // CRITICAL: cancel any pending deferred display:none on the product
      // selector AND every RPG shop. Without this, a stale hide timer from
      // a previous nav (scheduled in navigateTo or hideUnnecessaryElements)
      // can fire during our opacity transition and set display:none,
      // leaving the shop UI invisible. This is the explicit-cancellation
      // half of the race fix; the generation counter in _scheduleElementHide
      // is the safety net.
      this._cancelElementHide(this.elements.productSelector);
      this.elements.rpgShops.forEach(shop => this._cancelElementHide(shop));

      // Reset any previous state and prepare for fade-in
      this.elements.productSelector.style.transition = 'none';
      this.elements.productSelector.style.visibility = 'visible';
      this.elements.productSelector.style.display = 'flex';
      this.elements.productSelector.style.opacity = '0';
      this.elements.productSelector.style.pointerEvents = 'none';

      // Force a reflow to ensure the transition works
      this.elements.productSelector.offsetHeight;

      // Enable transitions and fade in
      this.elements.productSelector.style.transition = '';
      this.elements.productSelector.style.opacity = '1';
      this.elements.productSelector.style.pointerEvents = 'auto';

      // Wait for the transition to complete
      await this.waitForTransition(this.elements.productSelector);
      (window.HDLOG||function(){})('[HD] showMerchantsDen PHASE4 done');
    }

    // PHASE 5: Show headers
    this.updateHeaderVisibility(true);
    // Hide world map overlay if visible.
    // Phase 3 refactor: visibility is now CSS-driven via <html data-view>.
    // When the new view (not world-map) is set on the html attribute, the
    // CSS rule `html[data-view="world-map"] .map-section { visible }` no
    // longer matches → the section reverts to its default hidden state
    // (opacity:0, visibility:hidden, pointer-events:none) via the existing
    // CSS in rpg-map-modal.liquid. We only keep the .visible class removal
    // here as a defense-in-depth for legacy code paths still doing
    // .querySelector('.map-section.visible').
    const mapOverlay = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || document.querySelector('.map-section');
    if (mapOverlay) {
      mapOverlay.classList.remove('visible');
    }
    this.currentView = 'merchants-den';
    if (window._mobileWarmTransitions) window._mobileWarmTransitions('merchants-den');
  }

  async showRpgShop(shopId) {
    (window.HDLOG||function(){})('StateManager: Showing RPG shop:', shopId);
    
    // Update RPG shops before showing
    this.updateRpgShops();
    
    // Find the specific shop
    const shop = this.elements.rpgShops.find(s => s.id === shopId);
    if (!shop) {
      console.error('StateManager: RPG shop not found:', shopId);
      return;
    }

    // CRITICAL: cancel any pending deferred hide on this shop before showing.
    // Without this, a stale display:none from a prior navigateTo /
    // hideUnnecessaryElements can fire during our opacity transition and
    // hide the just-shown sub-panel — symptom: model video + info card
    // don't render even though the shop "opened".
    this._cancelElementHide(shop);

    // Show the shop
    shop.style.display = 'block';
    await new Promise(resolve => setTimeout(resolve, 50));
    shop.style.opacity = '1';
    await this.waitForTransition(shop);

    // Update height after the shop is visible
    const activeContent = shop.querySelector('.activeContent');
    if (activeContent) {
      (window.HDLOG||function(){})('StateManager: Updating height for active content');
      // Find the updateHeight function in the shop's scope
      const updateHeight = shop.querySelector('script')?.textContent?.match(/function\s+updateHeight\(\)\s*{([^}]*)}/)?.[0];
      if (updateHeight) {
        // Create a new function from the matched code and execute it
        const updateHeightFunc = new Function(updateHeight);
        updateHeightFunc();
      } else {
        console.warn('StateManager: updateHeight function not found in shop scope');
      }
    }

    // Show both RPG header and regular header
    if (this.elements.header) {
      (window.HDLOG||function(){})('StateManager: Showing RPG header');
      this.elements.header.style.display = 'block';
      this.elements.header.style.opacity = '0';
      this.elements.header.style.pointerEvents = 'auto';
      // Force reflow
      this.elements.header.offsetHeight;
      this.elements.header.style.opacity = '1';
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.waitForTransition(this.elements.header);
    }

    // Show regular header if it exists
    if (this.elements.regularHeader) {
      (window.HDLOG||function(){})('StateManager: Showing regular header');
      // Remove any hiding styles
      this.elements.regularHeader.style.opacity = '1';
      this.elements.regularHeader.style.transform = 'none';
      this.elements.regularHeader.style.pointerEvents = 'auto';
      this.elements.regularHeader.style.transition = 'none';
      this.elements.regularHeader.style.display = 'block';
    }
  }

  waitForTransition(element) {
    return new Promise((resolve) => {
      const onTransitionEnd = () => {
        element.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      // Add both transitionend and webkitTransitionEnd for cross-browser support
      element.addEventListener('transitionend', onTransitionEnd);
      element.addEventListener('webkitTransitionEnd', onTransitionEnd);

      // Fallback timeout in case transition events don't fire
      setTimeout(() => {
        element.removeEventListener('transitionend', onTransitionEnd);
        element.removeEventListener('webkitTransitionEnd', onTransitionEnd);
        resolve();
      }, this.TRANSITION_DURATION + 100); // Add 100ms buffer
    });
  }

  waitForMediaReady(mediaEl, timeoutMs = 900) {
    if (!mediaEl) return Promise.resolve(false);

    // HAVE_FUTURE_DATA (3) is generally enough to avoid black frame / jank.
    if (mediaEl.readyState >= 3) return Promise.resolve(true);

    return new Promise((resolve) => {
      let done = false;

      const cleanup = () => {
        mediaEl.removeEventListener('loadeddata', onReady);
        mediaEl.removeEventListener('canplay', onReady);
      };

      const onReady = () => {
        if (done) return;
        done = true;
        cleanup();
        resolve(true);
      };

      mediaEl.addEventListener('loadeddata', onReady);
      mediaEl.addEventListener('canplay', onReady);

      setTimeout(() => {
        if (done) return;
        done = true;
        cleanup();
        resolve(false);
      }, timeoutMs);
    });
  }

  updateHeaderVisibility(showHeaders) {
    
    if (this.elements.header) {
      if (showHeaders) {
        (window.HDLOG||function(){})('   Showing RPG header');
        this.elements.header.style.display = 'block';
        this.elements.header.style.opacity = '0';
        this.elements.header.style.pointerEvents = 'auto';
        // Force reflow
        this.elements.header.offsetHeight;
        this.elements.header.style.opacity = '1';
      } else {
        (window.HDLOG||function(){})('   Hiding RPG header');
        this.elements.header.style.opacity = '0';
        this.elements.header.style.pointerEvents = 'none';
        setTimeout(() => {
          (window.HDLOG||function(){})('   Setting RPG header display to none');
          this.elements.header.style.display = 'none';
        }, this.TRANSITION_DURATION);
      }
    } else {
    }

    if (this.elements.regularHeader) {
      if (showHeaders) {
        this.elements.regularHeader.style.display = 'block';
        this.elements.regularHeader.style.opacity = '1';
        this.elements.regularHeader.style.pointerEvents = 'auto';
      } else {
        (window.HDLOG||function(){})('   Hiding regular header');
        this.elements.regularHeader.style.opacity = '0';
        setTimeout(() => {
          (window.HDLOG||function(){})('   Setting regular header display to none');
          this.elements.regularHeader.style.display = 'none';
        }, this.TRANSITION_DURATION);
      }
    } else {
      (window.HDLOG||function(){})('   Regular header element not found');
    }
    
  }

  async showGuide() {
    (window.HDLOG||function(){})('[HD] showGuide entry | currentView:', this.currentView, '| transitioning:', this.isTransitioning);
    if (this.currentView === 'guide') {
      (window.HDLOG||function(){})('[HD] showGuide bail: already in guide');
      return;
    }

    // ── Phase 5 refactor ─────────────────────────────────────────────
    // Guide visibility is now driven by `html[data-view="guide"]` CSS
    // (see guide-section.liquid). ViewManager.setView('guide') is called
    // at the START of navigateTo so by the time we get here the data-view
    // attribute is already 'guide' and the CSS transition to visible is
    // in flight.
    //
    // Same pattern as showQuest (Phase 4): the method is reduced to
    // orchestration (header, world-map hide) — visibility itself is
    // declarative CSS.

    await this.hideUnnecessaryElements('guide');

    this.elements.guideSection = document.querySelector('.guide-section');

    if (this.elements.guideSection) {
      // Deep-link: skip the entrance animation for instant reveal.
      if (this._isDeepLinkNavigation) {
        this._isDeepLinkNavigation = false;
        this.elements.guideSection.classList.add('no-transition');
        requestAnimationFrame(() => requestAnimationFrame(() => {
          this.elements.guideSection.classList.remove('no-transition');
        }));
      }
    } else {
      console.warn('StateManager: Guide section not found when trying to show it!');
    }

    // Header visibility — independent of view system.
    this.updateHeaderVisibility(true);

    // World map: legacy class removal (defense-in-depth, CSS already
    // handles the actual hide via data-view).
    const mapOverlay = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || document.querySelector('.map-section');
    if (mapOverlay) mapOverlay.classList.remove('visible');

    // Update current view after everything is complete
    this.currentView = 'guide';
    if (window._mobileWarmTransitions) window._mobileWarmTransitions('guide');
    (window.HDLOG||function(){})('[HD] showGuide done');
  }

  async showQuest() {
    (window.HDLOG||function(){})('[HD] showQuest entry | currentView:', this.currentView, '| transitioning:', this.isTransitioning);
    if (this.currentView === 'quest') {
      (window.HDLOG||function(){})('[HD] showQuest bail: already in quest');
      return;
    }

    // ── Phase 4 refactor ────────────────────────────────────────────────
    // Quest visibility is now driven by `html[data-view="quest"]` CSS
    // (see quest-section.liquid). ViewManager.setView('quest') is called
    // at the START of navigateTo (line ~580), so by the time we get here
    // the data-view attribute is already 'quest' and the CSS transition
    // to visible is in flight.
    //
    // What used to be ~40 lines of inline-style mutations + class toggles
    // + double-rAF dance + per-item transition-delay setting is now
    // entirely declarative CSS. This method's job is reduced to:
    //   1. Hide other UI (legacy imperative — converted in later phases)
    //   2. Reset quest's INTERNAL state (selected item, active tab)
    //   3. Lock page scroll
    //   4. Show the header
    //
    // The visual transition is handled by CSS without any JS coordination.

    await this.hideUnnecessaryElements('quest');

    // Re-query in case the section was dynamically loaded after init.
    this.elements.questSection = document.querySelector('.quest-section');

    if (this.elements.questSection) {
      // Reset quest's own UI state: clear quest-selection, switch to main tab.
      // This is internal to the quest section, NOT visibility — keep as JS.
      if (typeof window.resetQuestState === 'function') {
        window.resetQuestState();
      }

      // Deep-link: skip the entrance fade so users land instantly when
      // they hit a /quest URL. Adds a one-frame .no-transition class.
      if (this._isDeepLinkNavigation) {
        this._isDeepLinkNavigation = false;
        this.elements.questSection.classList.add('no-transition');
        requestAnimationFrame(() => requestAnimationFrame(() => {
          this.elements.questSection.classList.remove('no-transition');
        }));
      }
    } else {
      console.warn('StateManager: Quest section not found when trying to show it!');
    }

    // Page scroll lock — quest UI is a fixed overlay, body must not scroll.
    this.lockQuestPageScroll();

    // Header visibility — independent of view system, stays imperative.
    this.updateHeaderVisibility(true);

    // World map: hide via legacy class removal (defense-in-depth; the
    // primary mechanism is the data-view change driven by ViewManager).
    const mapOverlay = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || document.querySelector('.map-section');
    if (mapOverlay) mapOverlay.classList.remove('visible');

    // Update current view after everything is complete
    this.currentView = 'quest';
    if (window._mobileWarmTransitions) window._mobileWarmTransitions('quest');
    (window.HDLOG||function(){})('[HD] showQuest done | quest section classes:', this.elements.questSection?.classList.toString(), '| opacity:', this.elements.questSection?.style.opacity);
  }

  async showWorldMap() {
    if (this.currentView === 'world-map') {
      (window.HDLOG||function(){})('Already in world map, skipping');
      return;
    }

    (window.HDLOG||function(){})('Showing world map');
    await this.navigateTo('world-map');
  }

  // Internal method for actually displaying the world map (called by navigateTo)
  async _displayWorldMap() {
    (window.HDLOG||function(){})('StateManager: Displaying world map as full state');
    
    // Clear any pending timeouts for world map to prevent race conditions
    const mapTimeoutKey = 'world-map-hide';
    if (this.pendingTimeouts.has(mapTimeoutKey)) {
      (window.HDLOG||function(){})('StateManager: Clearing pending world map hide timeout');
      clearTimeout(this.pendingTimeouts.get(mapTimeoutKey));
      this.pendingTimeouts.delete(mapTimeoutKey);
    }
    
    // Hide all other sections first
    await this.hideUnnecessaryElements('world-map');
    
    // Show headers for world map (same as other states)
    this.updateHeaderVisibility(true);
    
    // Find the map section and show it as a full state
    const mapSection = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || 
                      document.querySelector('.map-section') ||
                      document.querySelector('.rpg-map-modal-section .map-section');
    
    if (mapSection) {
      // Phase 3 refactor: visibility is now CSS-driven.
      // ViewManager.setView('world-map') sets <html data-view="world-map">
      // which triggers the CSS rule in rpg-map-modal.liquid that fades the
      // .map-section in (opacity:1, visibility:visible, pointer-events:auto)
      // with the existing 500ms transition.
      //
      // We still toggle the .visible class as a defense-in-depth signal for
      // legacy code paths (e.g. header.liquid querySelector('.map-section.visible')).
      // The 7 inline style mutations that used to live here are removed —
      // they were duplicating what CSS already does, and were the source of
      // state-desync bugs when the timing got off.
      // Other views leave inline hide styles behind. Clear them at the actual
      // reveal, after awaited teardown; the earlier data-view observer can run
      // before that teardown finishes and cannot own this handoff.
      ['display', 'opacity', 'visibility', 'pointer-events'].forEach(property => {
        mapSection.style.removeProperty(property);
      });
      mapSection.classList.add('visible');
      (window.HDLOG||function(){})('[HD] world-map shown (CSS-driven via data-view)');
    } else {
      console.warn('StateManager: World map section not found. Check if rpg-map-modal section is properly loaded.');
      
      // Try to find any map-related sections for debugging
      const mapSections = document.querySelectorAll('.map-section, .rpg-map-modal-section');
      (window.HDLOG||function(){})('StateManager: Available map sections:', mapSections);
    }
  }


  async hideUnnecessaryElements(view) {
    
    const hidePromises = [];
    
    // Always hide title screen unless we're showing it
    if (this.elements.titleScreen && view !== 'main-menu') {
      // Only hide if it's currently visible
      if (this.elements.titleScreen.style.display !== 'none' && this.elements.titleScreen.style.opacity !== '0') {
        this.elements.titleScreen.style.opacity = '0';
        this.elements.titleScreen.style.pointerEvents = 'none';
        hidePromises.push(new Promise(resolve => {
          setTimeout(() => {
            this.elements.titleScreen.style.display = 'none';
            resolve();
          }, this.TRANSITION_DURATION);
        }));
      } else {
      }
    } else if (this.elements.titleScreen && view === 'main-menu') {
      (window.HDLOG||function(){})('   Title screen current state:', {
        display: this.elements.titleScreen.style.display,
        opacity: this.elements.titleScreen.style.opacity,
        pointerEvents: this.elements.titleScreen.style.pointerEvents
      });
    } else if (!this.elements.titleScreen) {
    }

    // Only hide product selector if we're not in merchant's den
    if (this.elements.productSelector && view !== 'merchants-den') {
      // Only hide if it's currently visible
      if (this.elements.productSelector.style.display !== 'none' && this.elements.productSelector.style.opacity !== '0') {
        (window.HDLOG||function(){})('   Product selector is visible, hiding it');
        this.elements.productSelector.style.opacity = '0';
        this.elements.productSelector.style.pointerEvents = 'none';
        // Critical: when entering quest, quest UI intentionally starts at opacity:0 for its entrance choreography.
        // If the product selector is still "display:flex" during its fade-out, it can flash over the quest page.
        // So for quest we hard-hide immediately; for other transitions we keep the fade.
        if (view === 'quest') {
          // For quest we hard-hide immediately (no fade) — cancel any pending
          // hide timer first so a stale one can't fire later.
          this._cancelElementHide(this.elements.productSelector);
          this.elements.productSelector.style.visibility = 'hidden';
          this.elements.productSelector.style.display = 'none';
        } else {
          hidePromises.push(new Promise(resolve => {
            // Cancellable + generation-stamped hide. resolves immediately
            // either way so hideUnnecessaryElements's Promise.all doesn't hang.
            this._scheduleElementHide(this.elements.productSelector, () => {
              (window.HDLOG||function(){})('   Setting product selector display to none');
              this.elements.productSelector.style.display = 'none';
            }, this.TRANSITION_DURATION);
            // Resolve the await-promise on TRANSITION_DURATION regardless of
            // whether the hide actually fired — show paths cancel the hide
            // but the caller still wants its promise to settle.
            setTimeout(resolve, this.TRANSITION_DURATION);
          }));
        }
      } else {
      }
    } else if (this.elements.productSelector && view === 'merchants-den') {
    } else if (!this.elements.productSelector) {
    }

    // Hide guide section if we're not in guide view
    if (this.elements.guideSection && view !== 'guide') {
      this.elements.guideSection.classList.remove('visible');
      // Background video is now handled in the transition logic
      // if (window.toggleGuideBgVideo) window.toggleGuideBgVideo('none');
    } else if (this.elements.guideSection && view === 'guide') {
    } else if (!this.elements.guideSection) {
    }

    // Hide quest section if we're not in quest view
    if (view !== 'quest') {
      this.unlockQuestPageScroll();
    }

    // Unlock main menu scroll when leaving
    if (view !== 'main-menu') {
      this.unlockMainMenuScroll();
    }

    // Unlock merchants den scroll when leaving
    if (view !== 'merchants-den') {
      this.unlockMerchantsDenScroll();
      this.merchantsDenProductSelected = false;
    }
    if (this.elements.questSection && view !== 'quest') {
      // Phase 4: visibility is CSS-driven via html[data-view]. Clear any
      // inline overrides so the CSS rule (which now hides the section
      // since data-view !== 'quest') wins.
      this.elements.questSection.classList.remove('visible');
      this.elements.questSection.style.opacity = '';
      this.elements.questSection.style.pointerEvents = '';
    }

    // Hide all RPG shops
    this.elements.rpgShops.forEach((shop, index) => {
      // Only hide if it's currently visible
      if (shop.style.display !== 'none' && shop.style.opacity !== '0') {
        (window.HDLOG||function(){})(`   Hiding RPG shop ${index} (${shop.id || 'no-id'})`);
        shop.style.opacity = '0';
        hidePromises.push(new Promise(resolve => {
          // Cancellable hide — same pattern as productSelector above.
          this._scheduleElementHide(shop, () => {
            (window.HDLOG||function(){})(`   Setting RPG shop ${index} display to none`);
            shop.style.display = 'none';
          }, this.TRANSITION_DURATION);
          setTimeout(resolve, this.TRANSITION_DURATION);
        }));
      } else {
      }
    });

    // Hide world map if we're not in world-map view
    if (view !== 'world-map') {
      const mapSection = document.querySelector('[id^="shopify-section-"][id*="__map_section"] .map-section') || 
                        document.querySelector('.map-section') ||
                        document.querySelector('.rpg-map-modal-section .map-section');
      
      if (mapSection && mapSection.style.display !== 'none') {
        mapSection.classList.remove('visible');
        mapSection.style.opacity = '0';
        mapSection.style.visibility = 'hidden';
        mapSection.style.pointerEvents = 'none';
        
        // Clear any existing timeout for world map
        const mapTimeoutKey = 'world-map-hide';
        if (this.pendingTimeouts.has(mapTimeoutKey)) {
          clearTimeout(this.pendingTimeouts.get(mapTimeoutKey));
        }
        
        hidePromises.push(new Promise(resolve => {
          const timeoutId = setTimeout(() => {
            // A previous view's delayed hide must not disable a reopened map.
            if (document.documentElement.getAttribute('data-view') !== 'world-map') {
              mapSection.style.display = 'none';
            }
            this.pendingTimeouts.delete(mapTimeoutKey);
            resolve();
          }, this.TRANSITION_DURATION);
          
          this.pendingTimeouts.set(mapTimeoutKey, timeoutId);
        }));
      } else {
      }
    } else {
    }
    
    // Wait for all hide operations to complete
    await Promise.all(hidePromises);
  }

  // Add a method to check if we can navigate
  canNavigate() {
    // If we're on main menu, only allow navigation to merchants-den, guide, or world-map
    if (this.currentView === 'main-menu') {
      return false; // This is handled by the navigateTo method's specific logic
    }
    return !this.isTransitioning;
  }

  // Helper method for timestamped logging
  logWithTimestamp(message, data = null) {
    const timestamp = Date.now();
    const timeSinceLastCall = timestamp - this.lastMainMenuCall;
    this.lastMainMenuCall = timestamp;
    
    const logMessage = `[${timestamp}] ${message}`;
    if (data) {
      (window.HDLOG||function(){})(logMessage, data, `(+${timeSinceLastCall}ms)`);
    } else {
      (window.HDLOG||function(){})(logMessage, `(+${timeSinceLastCall}ms)`);
    }
  }
}

// Initialize the state manager
const stateManager = new StateManager();

// Export for use in other files
window.stateManager = stateManager;
